<template>
  <div class="composer-harbor"
       :class="{
         'composer-harbor--expanded': expanded,
         'composer-harbor--plain': !showFormatting,
       }">
    <div v-if="expanded" class="composer-harbor__backdrop" @click="collapse"></div>

    <div class="composer-harbor__shell">
      <div v-if="expanded" class="composer-harbor__header">
        <span class="font-semi">Compose message</span>
        <button type="button"
                class="harbor-chat__icon-btn"
                aria-label="Collapse composer (Esc)"
                @click="collapse">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </div>

      <div v-if="attachments.length > 0" class="composer-harbor__attachments">
        <div v-for="a in attachments"
             :key="a.localId"
             class="composer-attach-chip"
             :class="{ 'composer-attach-chip--failed': a.status === 'failed' }">
          <i class="composer-attach-chip__icon"
             :class="iconForAttachment(a)"
             aria-hidden="true"></i>
          <div class="composer-attach-chip__meta">
            <span class="composer-attach-chip__name" :title="a.name">{{ a.name }}</span>
            <span class="composer-attach-chip__sub">
              <template v-if="a.status === 'uploading'">Uploading...</template>
              <template v-else-if="a.status === 'failed'">Upload failed</template>
              <template v-else-if="a.size">{{ formatSize(a.size) }}</template>
            </span>
          </div>
          <spinner v-if="a.status === 'uploading'" :inline="true"></spinner>
          <button v-else
                  type="button"
                  class="composer-attach-chip__remove"
                  aria-label="Remove attachment"
                  @click="removeAttachment(a)">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <input type="hidden" :id="editorInputId" />
      <trix-editor
        :id="editorId"
        :input="editorInputId"
        data-cy="composer-editor"
        class="composer-harbor__editor trix-content"
      ></trix-editor>

      <div v-if="editingItem && editingItem.status !== 'draft'" class="scheduled-banner">
        <i :class="editingBannerIcon" aria-hidden="true"></i>
        <template v-if="isEditingScheduled">
          <span>Sending</span>
          <input type="datetime-local"
                 :min="nowDatetimeLocal"
                 v-model="scheduledAtLocal"
                 class="form-control"
                 data-cy="composer-schedule-input"
                 aria-label="Scheduled send time" />
        </template>
        <span v-else-if="isEditingSent">Editing sent message — a prior version will be saved in history</span>
      </div>

      <div v-if="showSchedulePicker" class="schedule-picker-row">
        <input type="datetime-local"
               :min="nowDatetimeLocal"
               v-model="scheduledAtLocal"
               class="form-control"
               data-cy="composer-schedule-input"
               aria-label="Schedule send at" />
        <button type="button" class="btn btn-default btn-compact" @click="closeSchedulePicker">Cancel</button>
        <button type="button"
                class="btn btn-primary btn-compact"
                data-cy="composer-schedule-send"
                :disabled="loading || !canSchedule"
                @click="scheduleMessage">
          Schedule send
        </button>
      </div>

      <div class="composer-harbor__toolbar">
        <div class="composer-harbor__toolbar-left">
          <button type="button"
                  class="harbor-chat__icon-btn"
                  :title="expanded ? 'Collapse (Esc)' : 'Expand (⌘E)'"
                  :aria-label="expanded ? 'Collapse composer' : 'Expand composer'"
                  @click="toggleExpanded">
            <i :class="expanded ? 'fa-solid fa-down-left-and-up-right-to-center' : 'fa-solid fa-up-right-and-down-left-from-center'" aria-hidden="true"></i>
          </button>
          <button type="button"
                  class="harbor-chat__icon-btn"
                  :class="{ 'harbor-chat__icon-btn--active': showFormatting }"
                  data-cy="composer-format-toggle"
                  :title="showFormatting ? 'Hide formatting' : 'Show formatting'"
                  :aria-label="showFormatting ? 'Hide formatting' : 'Show formatting'"
                  :aria-pressed="showFormatting ? 'true' : 'false'"
                  @click="toggleFormatting">
            <i class="fa-solid fa-font" aria-hidden="true"></i>
          </button>
          <file-input v-if="!isEditingSent"
                      :multiple="true"
                      :uploader="uploader"
                      :accept="acceptedExtensions">
            <span class="harbor-chat__icon-btn composer-harbor__attach-btn"
                  role="button"
                  title="Attach files"
                  aria-label="Attach files">
              <i class="fa-solid fa-paperclip" aria-hidden="true"></i>
            </span>
          </file-input>
          <button v-if="canAddPoll"
                  type="button"
                  class="harbor-chat__icon-btn"
                  data-cy="composer-poll-btn"
                  title="Add poll"
                  aria-label="Add poll"
                  @click="showPollForm = true">
            <i class="fa-solid fa-chart-column" aria-hidden="true"></i>
          </button>
          <button v-if="!isEditingSent && !isEditingScheduled"
                  type="button"
                  class="harbor-chat__icon-btn"
                  :class="{ 'harbor-chat__icon-btn--active': showSchedulePicker }"
                  data-cy="composer-schedule-btn"
                  :title="showSchedulePicker ? 'Close scheduler' : 'Schedule send'"
                  :aria-label="showSchedulePicker ? 'Close scheduler' : 'Schedule send'"
                  :aria-pressed="showSchedulePicker ? 'true' : 'false'"
                  @click="toggleSchedulePicker">
            <i class="fa-regular fa-clock" aria-hidden="true"></i>
          </button>
        </div>
        <div class="composer-harbor__toolbar-right">
          <small v-if="error" class="text-danger m-r-sm">{{ errorMessage }}</small>
          <small v-else-if="!isEditingSent && autosaveState !== 'idle'"
                 class="composer-harbor__autosave-status m-r-sm"
                 :class="{ 'text-danger': autosaveState === 'error' }"
                 data-cy="composer-autosave-status"
                 aria-live="polite">
            <template v-if="autosaveState === 'saving'">
              <i class="fa-solid fa-circle-notch fa-spin m-r-xxs" aria-hidden="true"></i>Saving draft…
            </template>
            <template v-else-if="autosaveState === 'saved'">Draft saved</template>
            <template v-else-if="autosaveState === 'error'">Couldn't save draft</template>
          </small>
          <spinner v-if="loading" :inline="true"></spinner>
          <button v-if="editingItem && !showSchedulePicker"
                  type="button"
                  class="btn btn-default btn-compact m-r-xs"
                  data-cy="composer-cancel-edit"
                  @click="abandonEditing">
            Cancel edit
          </button>
          <button v-if="!showSchedulePicker"
                  type="button"
                  class="btn btn-primary btn-compact"
                  data-cy="composer-send"
                  :disabled="loading || !canPrimaryAction"
                  :title="sendShortcutLabel"
                  :aria-label="sendShortcutLabel"
                  @click="primaryAction">
            {{ primaryActionLabel }}
          </button>
        </div>
      </div>
    </div>

    <poll-form
      v-if="showPollForm"
      :group-id="group.id"
      @poll-created="onPollCreated"
      @poll-cancelled="onPollCancelled"
    ></poll-form>
  </div>
</template>

<script>
import moment from 'moment';
import FineUploaderS3 from 'fine-uploader-wrappers/s3';
import RSVP from 'rsvp';
import FileInput from 'vue-fineuploader/file-input.vue';
import Spinner from './spinner.vue';
import PollForm from './poll_form.vue';
import { errorableMix } from './mixins/errorable_mix';
import { isHeicFile, convertHeicToJpeg } from '../utilities/heic';
import { messageBody } from '../utilities/trix_content';
import { reportAttachmentUploadFailure, uploaderKeyFor } from '../../shared/upload_error_reporting';
import { AUTOSAVE_DEBOUNCE_MS, generateClientToken, readComposerHtml } from '../utilities/composer';
import {
  ALLOWED_EXTENSIONS,
  ACCEPT_FILES,
  iconForAttachment,
  formatSize,
} from '../utilities/attachment_types';

const FORMATTING_PREF_KEY = 'wiq.messaging.showFormatting';

function readShowFormattingPreference() {
  try {
    return window.localStorage.getItem(FORMATTING_PREF_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function writeShowFormattingPreference(value) {
  try {
    window.localStorage.setItem(FORMATTING_PREF_KEY, value ? '1' : '0');
  } catch (_) { /* noop */ }
}

export default {
  name: 'message-form',
  components: { Spinner, PollForm, FileInput },
  mixins: [errorableMix],
  props: {
    group: { type: Object, required: true },
    pollsEnabled: { type: Boolean, default: false },
    endpoint: { type: String, required: true },
    accessKey: { type: String, required: true },
    authToken: { type: String, required: true },
    // Distinguishes multiple composer instances for the same group (e.g. the
    // inline bar vs. a scheduled-edit modal) so their trix-editor DOM ids differ.
    instanceKey: { type: String, default: '' },
    // The inline composer auto-loads the group's live draft on mount; the
    // scheduled-edit surface sets this false and loads a specific item instead.
    autoLoadDraft: { type: Boolean, default: true },
    // A specific queue item (e.g. a scheduled message) to load for editing once
    // the editor is ready. Used by the scheduled-edit surface.
    initialItem: { type: Object, default: null },
    // A pre-resolved live draft (null = the viewer has none), letting the bar
    // paint populated on the first frame instead of flickering empty -> content.
    // Only honoured when liveDraftProvided is true: null and "not supplied" are
    // different answers, and only the former means we can skip the fetch.
    initialLiveDraft: { type: Object, default: null },
    // Assert that initialLiveDraft is the truth *right now*, so the fetch can be
    // skipped entirely. Only safe when the value was minted in the same page load
    // as this composer (i.e. server-rendered). A value cached from an earlier
    // list fetch must NOT set this: adopting a stale draft's client_token makes
    // autosave upsert onto the live row and last-writer-wins over newer content
    // from another device, with no 409 to catch it. Leave false and let
    // loadLiveDraft reconcile.
    liveDraftProvided: { type: Boolean, default: false },
  },
  data() {
    const vm = this;

    const getKey = function (fineUploaderId) {
      const filename = vm.uploader.methods.getName(fineUploaderId);
      return new RSVP.Promise((resolve, reject) => {
        const params = { attachment: { name: filename, prefix: 'messages/' } };
        axios.post(vm.$apiService.attachmentsUrl(), params)
          .then((response) => {
            vm.pendingKeyForFineUploaderId[fineUploaderId] = response.data;
            resolve(response.data.key);
          })
          .catch((error) => { reject(error); });
      });
    };

    const uploader = new FineUploaderS3({
      options: {
        request: { endpoint: vm.endpoint, accessKey: vm.accessKey },
        maxConnections: 3,
        signature: {
          endpoint: '/api/v1/attachments/s3_signature',
          customHeaders: { Authorization: `Bearer ${vm.authToken}` },
        },
        uploadSuccess: {
          endpoint: '/api/v1/attachments/s3_confirm',
          customHeaders: { Authorization: `Bearer ${vm.authToken}` },
        },
        objectProperties: { key: getKey },
        validation: {
          allowedExtensions: ALLOWED_EXTENSIONS,
          acceptFiles: ACCEPT_FILES,
          sizeLimit: 52428800,
        },
        chunking: { enabled: false },
        resume: { enabled: false },
      },
    });

    return {
      loading: false,
      plainContent: '',
      trixEditor: null,
      showPollForm: false,
      expanded: false,
      editingItem: null,
      showFormatting: readShowFormattingPreference(),
      showSchedulePicker: false,
      scheduledAtLocal: '',
      autoDraftId: null,
      autosaveState: 'idle',
      autosaveRequestCounter: 0,
      // Client-minted idempotency key for the current composition. Sent on every
      // create so autosave/send/schedule for one message converge on a single
      // server row (see MessagesController#create). Re-minted per composition.
      clientToken: null,
      // A live draft fetched before the trix editor finished initializing; applied
      // in trixInitHandler once the editor exists.
      pendingLiveDraft: null,
      // A queue item (from initialItem) to load once the editor is ready.
      pendingQueueItem: null,
      uploader,
      attachments: [],
      pendingKeyForFineUploaderId: {},
      acceptedExtensions: ACCEPT_FILES,
    };
  },
  computed: {
    editorId() {
      return `msg-editor-${this.group.id}${this.instanceKey}`;
    },
    editorInputId() {
      return `msg-editor-input-${this.group.id}${this.instanceKey}`;
    },
    placeholder() {
      return this.group ? `Message ${this.group.name}...` : '';
    },
    uploadedAttachments() {
      return this.attachments.filter((a) => a.status === 'uploaded');
    },
    hasUploadInFlight() {
      return this.attachments.some((a) => a.status === 'uploading');
    },
    canSend() {
      if (this.hasUploadInFlight) return false;
      return this.plainContent.trim().length > 0 || this.uploadedAttachments.length > 0;
    },
    canSchedule() {
      if (!this.canSend) return false;
      if (!this.scheduledAtLocal) return false;
      return moment(this.scheduledAtLocal).isAfter(moment());
    },
    // A poll posts straight to the messages endpoint and sends on submit (see
    // poll_form.vue — no status, so the server defaults it to "sent"); it can't
    // be drafted, queued, or attached to a message being edited. Hide the entry
    // point wherever this composer is about to do anything other than send now,
    // rather than offer a button that would quietly ignore the surrounding state.
    canAddPoll() {
      return this.pollsEnabled
        && !this.isEditingSent
        && !this.isEditingScheduled
        && !this.showSchedulePicker;
    },
    nowDatetimeLocal() {
      return moment().format('YYYY-MM-DDTHH:mm');
    },
    sendShortcutLabel() {
      const isMac = typeof navigator !== 'undefined'
        && /Mac|iPod|iPhone|iPad/.test(navigator.platform || '');
      const verb = this.primaryActionLabel;
      return isMac ? `${verb} (⌘ + Enter)` : `${verb} (Ctrl + Enter)`;
    },
    // Label + gating for the primary composer button. Editing a scheduled message
    // saves it (keeps it scheduled) rather than sending it now; a sent-message edit
    // saves a new version; otherwise it sends.
    primaryActionLabel() {
      if (this.isEditingSent) return 'Save edit';
      if (this.isEditingScheduled) return 'Save';
      return 'Send';
    },
    canPrimaryAction() {
      // Editing an existing scheduled message only needs content + a time. Allow a
      // past time (the message is overdue and will send on the next job run) —
      // requiring a future time here would gray out Save on an already-due message.
      // New scheduling (the picker) still requires a future time via canSchedule.
      if (this.isEditingScheduled) return this.canSend && !!this.scheduledAtLocal;
      return this.canSend;
    },
    isEditingSent() {
      return !!(this.editingItem && this.editingItem.status === 'sent');
    },
    isEditingScheduled() {
      return !!(this.editingItem && this.editingItem.status === 'scheduled');
    },
    editingBannerIcon() {
      if (!this.editingItem) return '';
      if (this.editingItem.status === 'scheduled') return 'fa-regular fa-clock';
      if (this.isEditingSent) return 'fa-solid fa-pencil';
      return 'fa-regular fa-file-lines';
    },
  },
  created() {
    this.autosaveDraft = _.debounce(this.performAutosave.bind(this), AUTOSAVE_DEBOUNCE_MS);
    this.clientToken = generateClientToken();
    // iMessage-style: the inline composer auto-populates with the group's single
    // live draft (if any) and adopts its token. The scheduled-edit surface opts
    // out (autoLoadDraft: false) and loads a specific item instead.
    if (this.initialItem) {
      this.pendingQueueItem = this.initialItem; // applied when the editor is ready
    } else if (this.autoLoadDraft && this.liveDraftProvided) {
      // Already resolved server-side; a null here means "no draft", not "unknown",
      // so there is nothing left to fetch.
      if (this.initialLiveDraft) this.applyLiveDraft(this.initialLiveDraft);
    } else if (this.autoLoadDraft) {
      this.loadLiveDraft();
    }
  },
  mounted() {
    document.addEventListener('trix-initialize', this.trixInitHandler);
    document.addEventListener('keydown', this.handleGlobalKeydown);
    document.addEventListener('trix-file-accept', this.handleTrixFileAccept);
    this.wireUploaderEvents();
  },
  destroyed() {
    document.body.classList.remove('composer-overlay-open');
    document.removeEventListener('trix-initialize', this.trixInitHandler);
    document.removeEventListener('keydown', this.handleGlobalKeydown);
    document.removeEventListener('trix-file-accept', this.handleTrixFileAccept);
    if (this.autosaveDraft && this.autosaveDraft.cancel) this.autosaveDraft.cancel();
    const el = document.getElementById(this.editorId);
    if (el) {
      el.removeEventListener('keydown', this.handleEditorKeydown, true);
      el.removeEventListener('trix-change', this.handleTrixChange);
    }
  },
  methods: {
    handleTrixFileAccept(event) {
      if (event.target.id !== this.editorId) return;
      event.preventDefault();
    },
    wireUploaderEvents() {
      const vm = this;
      vm.uploader.on('submit', (id) => {
        const file = vm.uploader.methods.getFile(id);
        if (!file || !isHeicFile(file)) return true;

        // Browsers other than Safari can't render HEIC, so convert to JPEG and
        // submit that instead. Returning false cancels this raw HEIC upload.
        convertHeicToJpeg(file)
          .then((converted) => vm.uploader.methods.addFiles([converted]))
          .catch(() => {
            vm.$notificationManager.emit('show-toast', `Could not convert ${file.name}`, false);
          });
        return false;
      });
      vm.uploader.on('submitted', (id) => {
        const name = vm.uploader.methods.getName(id);
        const size = vm.uploader.methods.getSize(id);
        vm.attachments.push({
          localId: `upload-${id}`,
          fineUploaderId: id,
          id: null,
          name,
          size,
          status: 'uploading',
        });
      });
      vm.uploader.on('complete', (id, name, response) => {
        const chip = vm.attachments.find((a) => a.fineUploaderId === id);
        if (!chip) return;
        if (response && response.success) {
          const attachment = vm.pendingKeyForFineUploaderId[id];
          chip.id = attachment ? attachment.id : null;
          chip.status = 'uploaded';
          delete vm.pendingKeyForFineUploaderId[id];
          vm.autosaveDraft();
        } else {
          // s3_confirm answers 404 rather than raising, so this failure produces no
          // server-side incident -- report it from here (see AppSignal #2343).
          const pending = vm.pendingKeyForFineUploaderId[id];
          reportAttachmentUploadFailure({
            reason: 'confirm_rejected',
            source: 'message_form',
            fineUploaderId: id,
            fileName: name,
            expectedKey: pending ? pending.key : null,
            uploaderKey: uploaderKeyFor(vm.uploader, id),
            response,
          });
          chip.status = 'failed';
          vm.$notificationManager.emit('show-toast', `Upload failed: ${name}`, false);
        }
      });
      vm.uploader.on('error', (id) => {
        const chip = vm.attachments.find((a) => a.fineUploaderId === id);
        if (chip) chip.status = 'failed';
      });
    },
    trixInitHandler(event) {
      if (event.target.id !== this.editorId) return;
      this.trixEditor = event.target.editor;
      if (this.placeholder) {
        event.target.setAttribute('placeholder', this.placeholder);
      }
      event.target.addEventListener('keydown', this.handleEditorKeydown, true);
      event.target.addEventListener('trix-change', this.handleTrixChange);
      // Content that arrived before the editor was ready — apply it now.
      if (this.pendingQueueItem) {
        const item = this.pendingQueueItem;
        this.pendingQueueItem = null;
        this.loadQueueItem(item);
      } else if (this.pendingLiveDraft) {
        this.hydrateContent(this.pendingLiveDraft);
        this.pendingLiveDraft = null;
      }
    },
    handleTrixChange() {
      if (this.trixEditor) {
        this.plainContent = this.trixEditor.getDocument().toString();
      }
      if (this.autosaveState === 'saved') this.autosaveState = 'idle';
      this.autosaveDraft();
    },
    handleEditorKeydown(e) {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.isComposing) {
        if (this.showSchedulePicker) return;
        e.preventDefault();
        e.stopPropagation();
        this.primaryAction();
      }
    },
    primaryAction() {
      if (this.isEditingSent) {
        this.updateSentMessage();
      } else if (this.isEditingScheduled) {
        this.scheduleMessage(); // save edits, keep it scheduled (at the chosen time)
      } else {
        this.createMessage();
      }
    },
    handleGlobalKeydown(e) {
      if (e.key === 'Escape' && this.expanded) {
        e.preventDefault();
        this.collapse();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'e' || e.key === 'E')) {
        const active = document.activeElement;
        if (active && active.id === this.editorId) {
          e.preventDefault();
          this.toggleExpanded();
        }
      }
    },
    toggleExpanded() {
      this.expanded = !this.expanded;
      if (this.expanded) {
        document.body.classList.add('composer-overlay-open');
      } else {
        document.body.classList.remove('composer-overlay-open');
      }
      this.$nextTick(() => {
        const el = document.getElementById(this.editorId);
        if (el && el.editor) {
          el.focus();
        }
      });
    },
    collapse() {
      if (!this.expanded) return;
      this.expanded = false;
      document.body.classList.remove('composer-overlay-open');
    },
    toggleFormatting() {
      this.showFormatting = !this.showFormatting;
      writeShowFormattingPreference(this.showFormatting);
    },
    onPollCreated() {
      this.showPollForm = false;
      this.$parent.$emit('message-created', this.group);
    },
    onPollCancelled() {
      this.showPollForm = false;
    },
    iconForAttachment,
    formatSize,
    removeAttachment(a) {
      if (a.fineUploaderId !== undefined && a.status !== 'uploaded') {
        this.uploader.methods.cancel(a.fineUploaderId);
      }
      this.attachments = this.attachments.filter((x) => x !== a);
      this.autosaveDraft();
    },
    clearComposer() {
      this.plainContent = '';
      this.editingItem = null;
      this.showSchedulePicker = false;
      this.scheduledAtLocal = '';
      this.attachments = [];
      this.pendingKeyForFineUploaderId = {};
      this.autoDraftId = null;
      this.autosaveState = 'idle';
      this.autosaveRequestCounter += 1;
      // Start a fresh composition identity so the next message can't upsert onto
      // the row we just sent/cleared.
      this.clientToken = generateClientToken();
      if (this.autosaveDraft && this.autosaveDraft.cancel) this.autosaveDraft.cancel();
      if (this.trixEditor) {
        this.trixEditor.loadHTML('');
      }
      const input = document.getElementById(this.editorInputId);
      if (input) input.value = '';
    },
    // Load an existing scheduled (or draft) item for editing — used by the
    // scheduled-edit surface. Addresses the row by its own token so saves upsert
    // it in place.
    loadQueueItem(item) {
      if (!this.trixEditor) return;
      this.autoDraftId = null;
      this.autosaveState = 'idle';
      this.autosaveRequestCounter += 1;
      this.clientToken = item.client_token || generateClientToken();
      if (this.autosaveDraft && this.autosaveDraft.cancel) this.autosaveDraft.cancel();
      this.editingItem = item;
      this.hydrateContent(item);
      if (item.status === 'scheduled' && item.scheduled_at) {
        this.scheduledAtLocal = moment(item.scheduled_at).format('YYYY-MM-DDTHH:mm');
      } else {
        this.scheduledAtLocal = '';
      }
    },
    // Fetch this group's single live draft and adopt it (identity + content), so
    // the bar picks up where the author left off on any device.
    loadLiveDraft() {
      const vm = this;
      axios.get(vm.$apiService.liveDraftUrl(vm.group.id))
        .then((res) => {
          if (res && res.status === 200 && res.data) vm.applyLiveDraft(res.data);
        })
        .catch(() => { /* transient; leave the empty composer with its fresh token */ });
    },
    applyLiveDraft(draft) {
      if (!draft) return;
      // Adopt the draft's identity so this device's autosave/send/schedule
      // converge on its row (and with other devices editing the same draft).
      this.clientToken = draft.client_token || this.clientToken;
      this.autoDraftId = draft.id;
      if (this.trixEditor) {
        this.hydrateContent(draft);
      } else {
        this.pendingLiveDraft = draft; // applied once trix initializes
      }
    },
    // The live draft was consumed (sent/scheduled) or superseded on another
    // device — re-fetch and either re-adopt the current draft or clear the bar.
    reconcileLiveDraft() {
      const vm = this;
      vm.autosaveRequestCounter += 1; // invalidate any in-flight autosave
      axios.get(vm.$apiService.liveDraftUrl(vm.group.id))
        .then((res) => {
          if (res && res.status === 200 && res.data) {
            vm.applyLiveDraft(res.data);
          } else {
            vm.clearComposer();
          }
        })
        .catch(() => {});
    },
    // Load an item's content + attachments into the editor. Plain `content`
    // carries raw newlines (drafts/scheduled without rich_content, inbound SMS);
    // convert them to <br> before loading or HTML collapses them (mirrors
    // message.vue). Rich content loads as-is.
    hydrateContent(item) {
      const html = item.rich_content
        || (item.content ? _.escape(item.content).replace(/\n/g, '<br>') : '');
      this.trixEditor.loadHTML(html || '');
      const input = document.getElementById(this.editorInputId);
      if (input) input.value = html;
      this.plainContent = this.trixEditor.getDocument().toString();
      const existing = item.attachments || [];
      this.attachments = existing.map((att) => ({
        localId: `existing-${att.id}`,
        id: att.id,
        name: att.name,
        size: null,
        status: 'uploaded',
      }));
    },
    abandonEditing() {
      // In the editor modal this closes it (discarding the edit); the inline
      // composer has no editingItem so this only fires inside the modal.
      this.$emit('cancel-edit');
      this.clearComposer();
    },
    openSchedulePicker() {
      if (!this.scheduledAtLocal) {
        this.scheduledAtLocal = moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm');
      }
      this.showSchedulePicker = true;
    },
    closeSchedulePicker() {
      this.showSchedulePicker = false;
    },
    toggleSchedulePicker() {
      if (this.showSchedulePicker) {
        this.closeSchedulePicker();
      } else {
        this.openSchedulePicker();
      }
    },
    readRichContent() {
      return readComposerHtml(this.editorInputId);
    },
    buildMessageParams(extra = {}) {
      return {
        message: {
          ...messageBody({ html: this.readRichContent(), plain: this.plainContent }),
          client_token: this.clientToken,
          ...extra,
        },
        attachments: this.uploadedAttachments.map((a) => ({ id: a.id })),
      };
    },
    performAutosave() {
      const vm = this;
      if (vm.isEditingSent) return;
      if (vm.editingItem && vm.editingItem.status === 'scheduled') return;
      if (vm.showPollForm) return;
      if (vm.hasUploadInFlight) return;

      const hasContent = vm.plainContent.trim().length > 0 || vm.uploadedAttachments.length > 0;
      const draftExists = !!vm.autoDraftId || (vm.editingItem && vm.editingItem.status === 'draft');
      // Don't create an empty live draft out of nothing. But once one exists, keep
      // it and overwrite it — including to empty — so clearing the bar syncs across
      // devices (keep-empty, never delete).
      if (!hasContent && !draftExists) return;

      vm.autosaveState = 'saving';
      const baseUrl = vm.$apiService.messagesUrl(vm.group.id);
      const requestId = ++vm.autosaveRequestCounter;
      const params = vm.buildMessageParams({ status: 'draft' });
      // Always create-by-token (idempotent upsert): repeated autosaves converge on
      // the single live-draft row.
      axios.post(baseUrl, params)
        .then((res) => {
          const newId = res && res.data ? res.data.id : null;
          if (requestId !== vm.autosaveRequestCounter) return;
          if (newId) vm.autoDraftId = newId;
          vm.autosaveState = 'saved';
        })
        .catch((error) => {
          if (requestId !== vm.autosaveRequestCounter) return;
          // 409 => the live draft was consumed (sent/scheduled) or superseded on
          // another device. Reconcile with the server rather than erroring.
          if (error && error.response && error.response.status === 409) {
            vm.reconcileLiveDraft();
            return;
          }
          vm.autosaveState = 'error';
        });
    },
    scheduleMessage: _.throttle(function () {
      const vm = this;
      // New scheduling requires a future time (canSchedule); editing an existing
      // scheduled message allows a past/overdue time (canPrimaryAction). The
      // relevant button is gated already, so accept either here.
      if (vm.loading || !vm.canSend || !vm.scheduledAtLocal) return;
      vm.error = false;
      vm.loading = true;
      vm.autosaveDraft.cancel();
      vm.autosaveRequestCounter += 1;

      const scheduledAtIso = moment(vm.scheduledAtLocal).toISOString();
      const params = vm.buildMessageParams({ status: 'scheduled', scheduled_at: scheduledAtIso });
      const baseUrl = vm.$apiService.messagesUrl(vm.group.id);
      // Every write goes through create-by-token: the client_token upserts, so a
      // new composition and a loaded queue item (its token set on load) both
      // resolve to a single row — draft->scheduled or an edit of an existing
      // scheduled send — with no duplicate and no id-addressed path.
      const promise = axios.post(baseUrl, params);

      promise.then(() => {
        vm.loading = false;
        vm.clearComposer();
        if (vm.expanded) vm.collapse();
        vm.$emit('scheduled-saved');
        vm.$notificationManager.emit('show-toast', 'Message scheduled', true);
      }).catch((error) => {
        vm.loading = false;
        if (vm.handledDraftConflict(error, 'This draft was already sent or scheduled elsewhere.')) return;
        vm.errorMessage = vm.parseErrorResponse(error);
        vm.error = true;
      });
    }, 500, { trailing: false }),
    createMessage: _.throttle(function () {
      const vm = this;
      if (vm.loading) return;
      if (!vm.canSend) {
        vm.errorMessage = 'Please enter a message';
        vm.error = true;
        return;
      }
      vm.error = false;
      vm.loading = true;
      vm.autosaveDraft.cancel();
      vm.autosaveRequestCounter += 1;

      const wasEditingQueueItem = !!vm.editingItem;
      const params = vm.buildMessageParams();
      const baseUrl = vm.$apiService.messagesUrl(vm.group.id);
      // Every send goes through create-by-token (status defaults to sent). The
      // client_token makes it an idempotent upsert: a new composition, a loaded
      // draft/scheduled item (its token set on load), an in-flight autosave, and
      // any retry all converge on one row and transition it to sent exactly once.
      // No duplicate, no id-addressed path, no delete.
      const request = axios.post(baseUrl, params);

      request
        .then(() => {
          vm.loading = false;
          vm.clearComposer();
          vm.$parent.$emit('message-created', vm.group);
          if (wasEditingQueueItem) vm.$emit('message-sent-from-queue');
          if (vm.expanded) vm.collapse();
          vm.$notificationManager.emit('show-toast', 'Message sent', true);
        })
        .catch((error) => {
          vm.loading = false;
          if (vm.handledDraftConflict(error, 'This draft was already sent or scheduled elsewhere.')) return;
          vm.errorMessage = vm.parseErrorResponse(error);
          vm.error = true;
        });
    }, 500, { trailing: false }),
    // A 409 on send/schedule means the live draft was consumed on another device.
    // Reconcile the bar with the server and surface a gentle note instead of an
    // error. Returns true when it handled the error.
    handledDraftConflict(error, message) {
      if (!(error && error.response && error.response.status === 409)) return false;
      if (this.autoLoadDraft) this.reconcileLiveDraft();
      this.$notificationManager.emit('show-toast', message, false);
      return true;
    },
    updateSentMessage: _.throttle(function () {
      const vm = this;
      if (vm.loading || !vm.canSend || !vm.isEditingSent) return;
      vm.error = false;
      vm.loading = true;
      vm.autosaveDraft.cancel();

      const url = `${vm.$apiService.messagesUrl(vm.group.id)}/${vm.editingItem.id}`;
      const params = {
        message: messageBody({ html: vm.readRichContent(), plain: vm.plainContent }),
      };

      axios.put(url, params)
        .then((response) => {
          vm.loading = false;
          vm.clearComposer();
          vm.$emit('message-edited', response.data.message || response.data);
          if (vm.expanded) vm.collapse();
          vm.$notificationManager.emit('show-toast', 'Message updated', true);
        })
        .catch((error) => {
          vm.loading = false;
          vm.errorMessage = vm.parseErrorResponse(error);
          vm.error = true;
        });
    }, 500, { trailing: false }),
  },
};
</script>
